import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Card, Radio, Button, Avatar } from "antd";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";
import { useAppSelector, useAppDispatch } from "@stores/redux/store";
import { fetchFacebookMessagesDetail } from "@features/messages/messagesSlice";
import { updatePageSyncStatus } from "@features/channels/fanpageSlice";
import { useChatStore } from "@stores/chatStore";
import {
  backfillFacebookConversations,
  getSyncStatus,
  getActiveSync,
  getFacebookConversationsWithMeta,
} from "@api/services/facebook";
import { getBaseURL } from "@config/api.config";
import type { FacebookPage } from "@interfaces/api.interface";
import { CHANNELS } from "@common";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "./styles.module.less";
import FacebookIcon from "@assets/facebook-icon.svg";
import ZaloIcon from "@assets/zalo-icon.svg";
import ShopeeIcon from "@assets/shopee-icon.svg";

interface FacebookPageWithPicture extends FacebookPage {
  picture?: {
    data?: {
      url?: string;
    };
  };
}

type SyncPeriod = "1month" | "3months" | "6months" | "1year";

interface SyncLogoAnimationProps {
  platform?: "facebook" | "zalo" | "shopee";
}

const SyncLogoAnimation = ({ platform = "facebook" }: SyncLogoAnimationProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const platformIcon = {
    facebook: FacebookIcon,
    zalo: ZaloIcon,
    shopee: ShopeeIcon,
  }[platform];

  const logos: Array<
    | { type: "kido"; content: string }
    | { type: "platform"; icon: string }
  > = [
    { type: "kido", content: "K" },
    { type: "platform", icon: platformIcon },
    { type: "kido", content: "K" },
    { type: "platform", icon: platformIcon },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % logos.length);
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  const currentLogo = logos[currentIndex];

  return (
    <div className={styles.syncLogoContainer}>
      <div className={styles.syncLogoWrapper}>
        {currentLogo.type === "kido" ? (
          <div className={styles.kidoLogo}>{currentLogo.content}</div>
        ) : (
          <div className={styles.platformLogoWrapper}>
            <img
              src={currentLogo.icon}
              alt={platform}
              className={styles.platformLogo}
              onError={() => {
                console.error("Failed to load icon:", platform, currentLogo.icon);
              }}
            />
          </div>
        )}
      </div>
      <div className={styles.syncArrows}>
        <SyncOutlined className={styles.syncArrowLeft} />
        <SyncOutlined className={styles.syncArrowRight} />
      </div>
    </div>
  );
};

interface SyncPageProps {
  selectedFanpageName?: string;
  onClose?: () => void;
  filteredConversations?: Array<{ id: string }>;
}

export const SyncPage = ({ selectedFanpageName, onClose, filteredConversations = [] }: SyncPageProps) => {
  const { success, error: showError } = useNotificationContext();
  const dispatch = useAppDispatch();
  const { pages: facebookPages } = useAppSelector((state) => state.fanpage);
  const messagesStore = useAppSelector((state) => state.messages);
  const isSyncing = useChatStore((state) => state.isSyncing);
  const setIsSyncing = useChatStore((state) => state.setIsSyncing);
  const setSyncProgress = useChatStore((state) => state.setSyncProgress);
  
  const grantPermissionUrl = useMemo(() => {
    const baseURL = getBaseURL();
    return `${baseURL}/api/facebook/onbehalf`;
  }, []);
  const setSyncItems = useChatStore((state) => state.setSyncItems);
  const selectConversation = useChatStore((state) => state.selectConversation);

  const activeFanpage = useMemo(() => {
    if (!selectedFanpageName) return null;
    return facebookPages.find((p) => p.name === selectedFanpageName) || null;
  }, [selectedFanpageName, facebookPages]);

  const selectedFanpageId = activeFanpage?.id || null;
  const activeFanpageName = activeFanpage?.name || null;
  const [selectedPeriod, setSelectedPeriod] = useState<SyncPeriod>("6months");
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusPollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastItemsSuccessRef = useRef<number>(0);
  const isPollingRef = useRef(false);
  const pollSyncStatusRef = useRef<((syncId: string) => Promise<void>) | null>(null);
  const setConversations = useChatStore((state) => state.setConversations);
  const abortControllerRef = useRef<AbortController | null>(null);

  const syncOptions = [
    { value: "1month", label: "1 tháng", days: 30 },
    { value: "3months", label: "3 tháng", days: 90 },
    { value: "6months", label: "6 tháng", days: 180 },
    { value: "1year", label: "1 năm", days: 365 },
  ] as const;

  const fetchConversations = useCallback(async () => {
    if (!selectedFanpageId || !activeFanpageName) return;

    // Cancel any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const result = await getFacebookConversationsWithMeta({
        pageId: selectedFanpageId,
        offset: 0,
        limit: 50,
        signal,
      });

      if (signal.aborted) return;

      const conversationsWithFanpage = result.conversations.map((conv) => ({
        ...conv,
        fanpageName: activeFanpageName,
      }));

      const { conversations: currentConversations } = useChatStore.getState();
      const conversationsFromOtherFanpages = currentConversations.filter(
        (conv) =>
          !(
            conv.fanpageName === activeFanpageName &&
            conv.channel === CHANNELS.FACEBOOK
          )
      );

      const allConversations = [
        ...conversationsWithFanpage,
        ...conversationsFromOtherFanpages,
      ];
      setConversations(allConversations);
    } catch (error) {
      if (!(error instanceof Error && error.name === "AbortError")) {
        console.error("Error fetching conversations:", error);
      }
    }
  }, [selectedFanpageId, activeFanpageName, setConversations]);

  const pollSyncStatus = useCallback(async (syncId: string) => {
    if (!selectedFanpageId || isPollingRef.current) {
      return;
    }

    const currentPage = facebookPages.find((p) => p.id === selectedFanpageId);
    if (currentPage?.messageSyncStatus !== "IN_PROGRESS") {
      isPollingRef.current = false;
      setIsSyncing(false);
      return;
    }

    if (statusPollingRef.current) {
      clearTimeout(statusPollingRef.current);
      statusPollingRef.current = null;
    }

    isPollingRef.current = true;
    const startTime = Date.now();
    const POLL_INTERVAL = 2000; 

    try {
      const statusResponse = await getSyncStatus({
        syncId,
        pageId: selectedFanpageId,
      });

      if (statusResponse.code === 0 && statusResponse.data) {
        const { status, progressPercentage, itemsSuccess, totalItems } = statusResponse.data;
        setSyncProgress(progressPercentage);
        setSyncItems(itemsSuccess, totalItems || 0);
        if (itemsSuccess !== lastItemsSuccessRef.current) {
          lastItemsSuccessRef.current = itemsSuccess;
          await fetchConversations();
        }
        
        if (status === "RUNNING") {
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, POLL_INTERVAL - elapsedTime);
          isPollingRef.current = false;
          statusPollingRef.current = setTimeout(() => {
            pollSyncStatusRef.current?.(syncId);
          }, remainingTime);
        } else if (status === "COMPLETED") {
          isPollingRef.current = false;
          await fetchConversations();
          setIsSyncing(false);
          setSyncProgress(100);
          setSyncItems(0, 0);
          
          if (selectedFanpageId) {
            dispatch(
              updatePageSyncStatus({
                pageId: selectedFanpageId,
                messageSyncStatus: "SYNCED",
              })
            );
          }         
          success("Đồng bộ tin nhắn thành công!");
        } else if (status === "FAILED") {
          isPollingRef.current = false;
          setIsSyncing(false);
          setSyncProgress(0);
          setSyncItems(0, 0);
          
          if (selectedFanpageId) {
            dispatch(
              updatePageSyncStatus({
                pageId: selectedFanpageId,
                messageSyncStatus: "NOT_SYNCED",
              })
            );
          }
          showError("Đồng bộ tin nhắn thất bại");
        } else {
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, POLL_INTERVAL - elapsedTime);
          isPollingRef.current = false;
          statusPollingRef.current = setTimeout(() => {
            pollSyncStatusRef.current?.(syncId);
          }, remainingTime);
        }
      } else {
        isPollingRef.current = false;
        setIsSyncing(false);
        setSyncProgress(0);
        setSyncItems(0, 0);
        
        if (selectedFanpageId) {
          dispatch(
            updatePageSyncStatus({
              pageId: selectedFanpageId,
              messageSyncStatus: "NOT_SYNCED",
            })
          );
        }
        
        showError(statusResponse.message || "Có lỗi xảy ra trong quá trình đồng bộ. Vui lòng thử lại.");
      }
    } catch (error) {
      isPollingRef.current = false;
      setIsSyncing(false);
      setSyncProgress(0);
      setSyncItems(0, 0);
      
      if (selectedFanpageId) {
        dispatch(
          updatePageSyncStatus({
            pageId: selectedFanpageId,
            messageSyncStatus: "NOT_SYNCED",
          })
        );
      }
      
      console.error("Error polling sync status:", error);
      showError(
        error instanceof Error
          ? error.message
          : "Có lỗi xảy ra trong quá trình đồng bộ. Vui lòng thử lại."
      );
    }
  }, [selectedFanpageId, facebookPages, setIsSyncing, setSyncProgress, setSyncItems, dispatch, success, showError, fetchConversations]);

  useEffect(() => {
    pollSyncStatusRef.current = pollSyncStatus;
  }, [pollSyncStatus]);

  const handleSync = async () => {
    if (!selectedFanpageId) return;
    const selectedOption = syncOptions.find((opt) => opt.value === selectedPeriod);
    if (!selectedOption) return;
    const endTimeMs = Date.now();
    const startTimeMs = endTimeMs - selectedOption.days * 24 * 60 * 60 * 1000;

    setIsSyncing(true);
    setSyncProgress(0);
    setSyncItems(0, 0); 
    lastItemsSuccessRef.current = 0;

    try {
      const response = await backfillFacebookConversations({
        pageId: selectedFanpageId,
        startTimeMs,
        endTimeMs,
      });
      
      if (response.code === 0 || response.code === 200) {
        const syncId = 
          response.data?.syncId || 
          (response as { syncId?: string }).syncId ||
          ((response as { data?: { syncId?: string } }).data?.syncId);
        
        if (syncId && typeof syncId === "string") {
          if (selectedFanpageId) {
            dispatch(
              updatePageSyncStatus({
                pageId: selectedFanpageId,
                messageSyncStatus: "IN_PROGRESS",
              })
            );
          }
          pollSyncStatus(syncId);
        } else {
          setIsSyncing(false);
          setSyncProgress(0);
          setSyncItems(0, 0); 
          showError("Không nhận được syncId từ server");
        }
      } else {
        setIsSyncing(false);
        setSyncProgress(0);
        setSyncItems(0, 0); 
        showError(response.message || "Đồng bộ tin nhắn thất bại");
      }
    } catch (error) {
      setIsSyncing(false);
      setSyncProgress(0);
      setSyncItems(0, 0); 
      console.error("Error syncing conversations:", error);
      showError("Đồng bộ tin nhắn thất bại. Vui lòng thử lại sau.");
    }
  };

  const handleCancel = () => {
    if (filteredConversations.length > 0 && selectedFanpageId) {
      const firstConversation = filteredConversations[0];
      selectConversation(firstConversation.id);
      const messagesAlreadyLoaded =
        messagesStore.messagesByConversation[firstConversation.id]?.length > 0;
      
      if (!messagesAlreadyLoaded) {
        dispatch(
          fetchFacebookMessagesDetail({
            conversationId: firstConversation.id,
            pageId: selectedFanpageId,
            offset: 0,
            limit: 100,
          })
        );
      }
    }
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const checkActiveSync = async () => {
      if (!selectedFanpageId) return;

      const messageSyncStatus = activeFanpage?.messageSyncStatus;
      if (messageSyncStatus !== "IN_PROGRESS") {
        return;
      }

      try {
        const activeSyncResponse = await getActiveSync({
          pageId: selectedFanpageId,
        });

        if (
          activeSyncResponse.code === 0 &&
          activeSyncResponse.data?.syncId
        ) {
          const syncId = activeSyncResponse.data.syncId;
          setIsSyncing(true);
          setSyncProgress(0);
          setSyncItems(0, 0);
          lastItemsSuccessRef.current = 0;
          pollSyncStatus(syncId);
        }
      } catch (error) {
        console.error("Error checking active sync:", error);
        dispatch(
          updatePageSyncStatus({
            pageId: selectedFanpageId,
            messageSyncStatus: "NOT_SYNCED",
          })
        );
        setIsSyncing(false);
      }
    };

    checkActiveSync();

    return () => {
      if (statusPollingRef.current) {
        clearTimeout(statusPollingRef.current);
        statusPollingRef.current = null;
      }
      isPollingRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedFanpageId, activeFanpage, dispatch, setIsSyncing, setSyncProgress, setSyncItems, pollSyncStatus]);

  useEffect(() => {
    const progressInterval = progressIntervalRef.current;
    const statusPolling = statusPollingRef.current;
    
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (statusPolling) {
        clearTimeout(statusPolling);
      }
    };
  }, []);

  return (
    <div className={styles.newPageContainer}>
      <Card className={styles.newPageCard}>
        <div className={styles.newPageContent}>
          <div className={styles.header}>
            <h2 className={styles.mainTitle}>
              Đồng bộ tin nhắn và bình luận từ Fanpage
            </h2>
            <p className={styles.subtitle}>
              Fanpage đã được kết nối. Hãy thiết lập thời gian đồng bộ để bắt đầu.
            </p>
          </div>

          <div className={styles.logoSection}>
            <div className={styles.newPageIconWrapper}>
              <div className={styles.newPageIcon}>
                <SyncLogoAnimation platform="facebook" />
              </div>
              <div className={styles.newPageBadge}>MỚI</div>
            </div>
          </div>

          {activeFanpage && (
            <div className={styles.fanpageSection}>
              <h3 className={styles.sectionTitle}>
                Fanpage đang kết nối
              </h3>
              <div className={styles.activeFanpageCard}>
                <div className={styles.fanpageItemContent}>
                  <Avatar
                    src={(activeFanpage as FacebookPageWithPicture).picture?.data?.url}
                    size={48}
                    className={styles.fanpageAvatar}
                  >
                    {activeFanpage.name?.[0]?.toUpperCase()}
                  </Avatar>
                  <div className={styles.fanpageInfo}>
                    <div className={styles.fanpageName}>{activeFanpage.name}</div>
                    <div className={styles.fanpageDescription}>
                      {activeFanpage.category || "Trang Facebook"}
                    </div>
                  </div>
                  <div className={styles.activeBadge}>
                    <span>Trang mới</span>
                  </div>
                </div>
              </div>
              <a href={grantPermissionUrl} target="_blank" rel="noopener noreferrer" className={styles.grantPermissionLink}>
                Muốn kết nối thêm Fanpage khác?{" "}
                <span className={styles.linkText}>Cấp quyền trang mới</span>
              </a>
            </div>
          )}

          {!isSyncing && (
            <div className={styles.periodSection}>
              <h3 className={styles.sectionTitle}>
                Chọn khoảng thời gian để tải về các cuộc hội thoại cũ
              </h3>
              <Radio.Group
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value as SyncPeriod)}
                className={styles.periodSelector}
                optionType="button"
                buttonStyle="solid"
              >
                {syncOptions.map((option) => (
                  <Radio.Button
                    key={option.value}
                    value={option.value}
                    className={styles.periodOption}
                  >
                    {option.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          )}


          <div className={styles.actionButtons}>
            <Button
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={isSyncing || filteredConversations.length === 0}
              icon={<CloseOutlined />}
            >
              Huỷ
            </Button>
            <Button
              type="primary"
              onClick={handleSync}
              loading={isSyncing}
              disabled={!selectedFanpageId || isSyncing}
              className={styles.syncButton}
              icon={<SyncOutlined />}
            >
              Bắt đầu đồng bộ
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

